import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiAutocomplete,
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiDatePicker,
  MiIcon,
  MiIconButton,
  MiInputMultiSelectBox,
  MiInputSelectBox,
  MiList,
  MiStackedTypography,
} from "@miplatform/components";
import { PAGINATION } from "@miplatform/constants";
import {
  IDateRangeISO,
  IQuickBooksAuditListResponse,
  QuickBookSyncStatus,
  QuickBooksType,
} from "@miplatform/types";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { mdiPrinterOutline, mdiSync } from "@mdi/js";
import { Box, Collapse, Divider, Typography } from "@mui/material";
import { QuickBooksIcon } from "../AccountingList/QuickBooksIcon";
import {
  Filters,
  LIST_CONFIG,
  LIST_ITEM_WIDTH,
  optionsType,
  sortByOptions,
  syncOptionValues,
  syncTypeOptions,
} from "./constants";
import { useAuditLogViewModel } from "./useAuditLogViewModel";

const TypedMiAlert = MiAlert as any;

const FilterComponent = React.memo<{
  customerOptions: optionsType[];
  communityOptions: optionsType[];
  filters: Filters;
  dateRange: IDateRangeISO | null;
  onDateChange: (newDateRange: IDateRangeISO | null) => void;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}>(
  ({
    customerOptions,
    communityOptions,
    filters,
    dateRange,
    onDateChange,
    setFilters,
  }) => {
    const handleSortChange = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        setFilters((prevFilters) => ({
          ...prevFilters,
          sortBy: event.target.value,
        }));
      },
      []
    );

    const handleSyncTypeChange = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        setFilters((prevFilters: Filters) => ({
          ...prevFilters,
          quickBooksType: event.target.value as Filters["quickBooksType"],
        }));
      },
      []
    );

    const handleSyncStatusChange = useCallback(
      (event: { target: { value: string[] | string } }) => {
        const value = Array.isArray(event.target.value)
          ? event.target.value
          : event.target.value.split(",");
        setFilters((prevFilters: Filters) => ({
          ...prevFilters,
          status: value as Filters["status"],
        }));
      },
      []
    );

    const handleStartDateChange = useCallback(
      (startDate: Date | null) => {
        if (!startDate) {
          onDateChange(null);
          return;
        }

        onDateChange({
          startDate: startDate.toISOString(),
          endDate:
            dateRange?.endDate && moment(dateRange.endDate) >= moment(startDate)
              ? dateRange.endDate
              : moment().toISOString(),
        });
      },
      [dateRange, onDateChange]
    );

    const handleEndDateChange = useCallback(
      (endDate: Date | null) => {
        if (!dateRange?.startDate && !endDate) {
          onDateChange(null);
          return;
        }

        const startDate = dateRange?.startDate
          ? moment(dateRange.startDate)
          : endDate;

        if (!startDate) {
          onDateChange(null);
          return;
        }

        onDateChange({
          startDate: startDate.toISOString(),
          endDate: endDate?.toISOString() || null,
        });
      },
      [dateRange, onDateChange]
    );

    const handleCustomerChange = useCallback(
      (_: SyntheticEvent, value: optionsType | null) => {
        setFilters((prevFilters: Filters) => ({
          ...prevFilters,
          accountId: value?.value || "",
        }));
      },
      []
    );

    const handleCommunityChange = useCallback(
      (_: SyntheticEvent, value: optionsType | null) => {
        setFilters((prevFilters: Filters) => ({
          ...prevFilters,
          communityId: value?.value || "",
        }));
      },
      []
    );

    return (
      <Box height="100%" flex={0.2}>
        <Typography variant="body2" fontWeight={600}>
          Filter & Sort
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Box>
          <Typography fontSize={12} fontWeight={600} my={2}>
            Sort By
          </Typography>
          <MiInputSelectBox
            options={sortByOptions}
            value={filters.sortBy}
            disableNone
            handleChange={handleSortChange}
          />
          <Typography fontSize={12} fontWeight={600} my={2}>
            Sync Type
          </Typography>
          <MiInputSelectBox
            options={syncTypeOptions}
            value={filters.quickBooksType}
            handleChange={handleSyncTypeChange}
            disableNone
          />
          <Typography fontSize={12} fontWeight={600} my={2}>
            Sync Status
          </Typography>
          <MiInputMultiSelectBox
            {...({
              menuItems: syncOptionValues,
              value: filters.status,
              disableNone: true,
              handleChange: handleSyncStatusChange,
              labelText: "Sync Status",
              emptyValueLabel: "All",
            } as any)}
          />
          <Typography fontSize={12} fontWeight={600} my={2}>
            Date Range
          </Typography>
          <Box sx={{ my: 2 }}>
            <MiDatePicker
              label="Start Date"
              value={dateRange?.startDate ? moment(dateRange.startDate) : null}
              onChange={handleStartDateChange}
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <MiDatePicker
              label="End Date"
              value={dateRange?.endDate ? moment(dateRange.endDate) : null}
              onChange={handleEndDateChange}
            />
          </Box>
          <MiButton
            title="Clear Date Range"
            onClick={() => onDateChange(null)}
            disabled={!dateRange?.startDate}
            sx={{ mt: 1, textDecoration: "underline", alignSelf: "flex-start" }}
          />
          <Typography fontSize={12} fontWeight={600} my={2}>
            Customer
          </Typography>
          <MiAutocomplete
            name="customerOptions"
            getOptionLabel={(option: optionsType) => option.label}
            options={customerOptions}
            value={
              customerOptions.find((opt) => opt.value === filters.accountId) ||
              null
            }
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            required
            onChange={handleCustomerChange}
          />
          <Typography fontSize={12} fontWeight={600} my={2}>
            Community
          </Typography>
          <MiAutocomplete
            name="communityOptions"
            getOptionLabel={(option: optionsType) => option.label}
            options={communityOptions}
            value={
              communityOptions.find(
                (opt) => opt.value === filters.communityId
              ) || null
            }
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            required
            onChange={handleCommunityChange}
          />
        </Box>
      </Box>
    );
  }
);

FilterComponent.displayName = "FilterComponent";

export const AuditLog: React.FC = () => {
  const { classes } = useStyles();
  const [syncingItemIds, setSyncingItemIds] = React.useState(new Set<string>());

  const {
    dateRange,
    getAuditLogs,
    onDateChange,
    handleSearch,
    getCommunities,
    getCustomers,
    auditLogs,
    setAuditLogs,
    customers: customerOptions,
    communities: communityOptions,
    search,
    isLoading,
    filters,
    setFilters,
    debouncedSearch,
    getAccount,
    handlePrintAudits,
    setDateRange,
    syncObject,
  } = useAuditLogViewModel();

  useEffect(() => {
    Promise.all([getCustomers(), getCommunities(), getAccount()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters({
        sortBy: "lastSyncDate",
        status: [],
        accountId: "",
        communityId: "",
        quickBooksType: "All",
      });
      setDateRange(null);
    }
  }, [debouncedSearch]);

  const refresh = useMemo(() => ({ dateRange, filters }), [dateRange, filters]);

  const canPrint = useMemo(
    () => !isLoading && auditLogs.length > 0,
    [isLoading, auditLogs.length]
  );

  const renderHomeAddress = useCallback(
    (item: IQuickBooksAuditListResponse) => {
      if (item.quickBooksType !== "Home") return null;

      const home = item.additionalData ? JSON.parse(item.additionalData) : {};
      const city = home?.city || "";
      const communityName = item?.communityName || "";
      const builderName = home?.builderName || "";

      return (
        <Typography
          fontSize={12}
          width={180}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
          color="grey"
        >
          {`${city} | ${communityName} | ${builderName}`}
        </Typography>
      );
    },
    []
  );

  const renderListTitle = useCallback((item: IQuickBooksAuditListResponse) => {
    const data = item.additionalData ? JSON.parse(item.additionalData) : {};

    const getTitleText = () => {
      switch (item.quickBooksType) {
        case QuickBooksType.Invoice:
          return data.invoiceNumber || " ";
        case QuickBooksType.Payment:
          return data.paymentNumber || " ";
        case QuickBooksType.Home:
        case QuickBooksType.Home_Retainage:
          return data.addressLine1 || " ";
        case QuickBooksType.Account_Retainage:
        case QuickBooksType.Account:
        case QuickBooksType.Vendor:
          return item.accountName || " ";
        case QuickBooksType.CreditMemo:
          return (
            JSON.parse(data.homeAddress || "{}").addressLine1 || "Credit Memo"
          );
        default:
          return item.quickBooksType;
      }
    };

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight={600} color="primary.main">
          {getTitleText()}
        </Typography>
        <QuickBooksIcon
          order={{ ...item, quickBooksStatus: item.status }}
          invoices={undefined}
        />
      </Box>
    );
  }, []);

  const sections = useMemo(
    () => [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "priority",
            maxWidth: 400,
            justifyContent: "flex-end",
            renderCell: (row: IQuickBooksAuditListResponse) => (
              <Box
                position="relative"
                display="flex"
                flexWrap="wrap"
                justifyContent="flex-end"
                gap={2}
              >
                {(LIST_CONFIG[row.quickBooksType]?.fields || []).map(
                  ({ label, fieldKey, valueGetter, width }) => (
                    <MiStackedTypography
                      key={fieldKey}
                      title={label}
                      titleProps={{ width: LIST_ITEM_WIDTH }}
                      description={
                        valueGetter
                          ? valueGetter(row)
                          : parseFieldValue(
                              getNestedValue(row, fieldKey),
                              label.toLowerCase().includes("date")
                                ? "--/--/--"
                                : undefined
                            )
                      }
                      descriptionProps={{ width: width || LIST_ITEM_WIDTH }}
                    />
                  )
                )}
              </Box>
            ),
          },
        ],
      },
    ],
    []
  );

  const renderItemActions = useCallback(
    (item: IQuickBooksAuditListResponse) => {
      const isSyncing = syncingItemIds.has(item.id);

      const handleSyncClick = async () => {
        setSyncingItemIds((prev) => new Set(prev).add(item.id));
        await syncObject(item);
        setSyncingItemIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      };

      return (
        <MiButton
          sx={{ width: 130 }}
          title="Sync"
          variant="outlined"
          startIcon={{
            path: mdiSync,
            iconSize: 1,
          }}
          disabled={item.status === QuickBookSyncStatus.Synced || isSyncing}
          loading={isSyncing}
          onClick={handleSyncClick}
        />
      );
    },
    [syncingItemIds]
  );

  const cardProps = useMemo(
    () => ({
      sections,
      renderTitle: renderListTitle,
      renderCardAction: () => null,
      leftCardProps: {
        fieldset: [
          {
            field: "homeId",
            valueGetter: renderHomeAddress,
          },
        ],
      },
      renderItemActions,
    }),
    [sections, renderListTitle, renderHomeAddress, renderItemActions]
  );

  const createMiList = useCallback(
    (isPrint = false) => (
      <MiList
        viewType="card"
        loading={isPrint ? false : isLoading}
        cardProps={cardProps}
        refresh={refresh}
        search={debouncedSearch}
        paginationMode={PAGINATION.SERVER}
        data={auditLogs}
        setData={setAuditLogs}
        dataProvider={getAuditLogs}
        defaultPageSize={25}
        hideListPagination={isPrint}
        emptyListProps={{
          text: "No Audit Logs found",
        }}
      />
    ),
    [
      isLoading,
      cardProps,
      refresh,
      debouncedSearch,
      auditLogs,
      setAuditLogs,
      getAuditLogs,
    ]
  );

  const displayList = useMemo(() => createMiList(false), [createMiList]);
  const printList = useMemo(() => createMiList(true), [createMiList]);

  const handlePrintClick = useCallback(() => {
    handlePrintAudits(printList);
  }, [handlePrintAudits, printList]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleSearch(e.target.value);
    },
    [handleSearch]
  );

  return (
    <>
      <MiBuildPageHeader
        title="Audit Log"
        search={search}
        loading={isLoading}
        onHandleSearch={handleSearchChange}
        actionComponent={
          <MiIconButton
            data-testid="mi-orders-print-icon-button"
            disabled={!canPrint}
            onClick={handlePrintClick}
            className={!canPrint ? classes.disabledIconButton : undefined}
          >
            <MiIcon path={mdiPrinterOutline} color="grey" />
          </MiIconButton>
        }
      />

      <MiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          flex={1}
          flexDirection="row"
          alignItems="center"
          gap={4}
          overflow="hidden"
        >
          <Box height="100%" flex={0.8} overflow="auto">
            <Collapse in={!!debouncedSearch}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                mb={2}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Results for{" "}
                  <Typography
                    component="span"
                    color="text.primary"
                    fontWeight={700}
                  >
                    {debouncedSearch}.
                  </Typography>
                </Typography>
                <Typography
                  variant="body1"
                  color="blue.main"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSearch("")}
                  sx={{ textDecoration: "underline" }}
                  fontWeight={700}
                >
                  Clear Search
                </Typography>
              </Box>
            </Collapse>

            <TypedMiAlert
              isOpen={!!debouncedSearch}
              title=""
              description={
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight={600}
                >
                  Date selection and filters reset while searching
                </Typography>
              }
            />

            {displayList}
          </Box>
          <Divider orientation="vertical" flexItem />
          <FilterComponent
            customerOptions={customerOptions}
            communityOptions={communityOptions}
            dateRange={dateRange}
            onDateChange={onDateChange}
            setFilters={setFilters}
            filters={filters}
          />
        </Box>
      </MiBox>
    </>
  );
};

const useStyles = makeStyles()(() => ({
  disabledIconButton: {
    opacity: 0.4,
    pointerEvents: "none",
  },
}));
